"use client";

import { useContext, useEffect, useState } from "react";

import { useImmer } from "use-immer";
import { trans } from "@/service/i18n/i18n";
// eslint-disable-next-line import/named
import { useForm } from "react-hook-form";

import { handleUserError } from "@/service/client-api/error-handler";
import { preSubmit } from "@/forms/validation";
import { Button } from "@/components/ui/button";
import { UserContext } from "@/contexts/user-context";
import { clientGet, clientPatch } from "@/service/client-api/client-api";

import { getAssetImage } from "@/service/image/asset";
import { logPermissionEvent } from "@/components/common/permission/service";
import { ResourceContext } from "@/contexts/resource-context";
import { createFormFields } from "@/components/common/permission/needed-action/student-need-to/become-medic/components/form-become-medic/model";
import { vestResolver } from "@hookform/resolvers/vest";
import { fromFormDefinitionToValidationSchema } from "@/forms/validation/validation-factory";
import {
  isDiplomaType,
  isDoctorOrDentistType,
  isOccupationalTherapist,
  isPublicHealthGraduate,
} from "@/service/user-groups";
import { NilDetails } from "@/components/common/nil-details";
import { SelectField } from "@/forms/fields/select-field/select-field";
import { TextField } from "@/forms/fields/text-field/text-field";
import { CheckboxField } from "@/forms/fields/checkbox-field/checkbox-field";
import { DateField } from "@/forms/fields/date-field/date-field";
import { Form } from "@/components/ui/form";
import { LoaderIcon } from "lucide-react";
import { FormError } from "@/forms/form-error";
import { Dialog } from "@/components/ui/dialog";
import { CroppedImageField } from "@/forms/fields/cropped-image-field/cropped-image-field";
import { FieldsContainer } from "@/forms/fields-container";

export const FormBecomeMedic = ({
  isNewsletterRequired,
  closeForm,
  setAccess,
}: {
  isNewsletterRequired: boolean;
  closeForm: () => void;
  setAccess: (value: any) => void;
}) => {
  const user = useContext(UserContext);
  const dataForType = user.dataForType;
  const [type, setType] = useState(null);

  const [fields, setFields] = useImmer(
    createFormFields(
      type,
      dataForType,
      user.defaultNewsletter,
      isNewsletterRequired,
    ),
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isModalOpen, setModalOpen] = useState(false);
  const { logView } = useContext(ResourceContext);

  const form = useForm({
    resolver: vestResolver(fromFormDefinitionToValidationSchema(fields)),
  });
  const { handleSubmit } = form;

  useEffect(() => {
    setFields(
      createFormFields(
        type,
        dataForType,
        user.defaultNewsletter,
        isNewsletterRequired,
      ),
    );
  }, [type]);

  if (Object.keys(fields).length === 0) {
    return null;
  }

  async function onSubmit(values: Record<keyof typeof fields, any>) {
    if (preSubmit(loading, setLoading, setError, setFields) === "BREAK") {
      return;
    }

    const oldSnapshot = (await clientGet("profile.info")) as any;

    const payload = {
      type: values.type,
      dataForType: {},
    } as {
      type: string;
      newsletter?: boolean;
      dataForType: Partial<Record<keyof typeof fields, any>>;
    };

    if (values.newsletter) {
      payload.newsletter = values.newsletter;
    }

    if (isDoctorOrDentistType(values.type)) {
      payload.dataForType = {
        licence: values.licence,
        birthdayDate: values.birthdayDate,
      };
    } else if (isDiplomaType(values.type)) {
      payload.dataForType = {
        verificationImage: {
          file: values.verificationImage,
        },
        firstName: values.firstName,
        secondName: values.secondName,
      };
    } else {
      payload.dataForType = {
        licence: values.licence,
        firstName: values.firstName,
        secondName: values.secondName,
      };
    }

    try {
      const response = await clientPatch("profile.update", {}, payload);

      if (response.firstTimeLicenceCompleted) {
        window?.dataLayer?.push({
          event: "firstTimeLicenceCompleted",
        } as never);
      }
    } catch (caughtError) {
      setLoading(false);
      handleUserError(caughtError, setError, setFields);
      return;
    }

    const { dataForType: newDataForType }: UserVM = await clientGet(
      "profile.info",
    );

    if (newDataForType["specializationsByNil"]?.length === 0) {
      try {
        await clientPatch(
          "profile.update",
          {},
          {
            dataForType: {
              status: "intern",
            },
          },
        );
      } catch (caughtError) {
        setLoading(false);
        handleUserError(caughtError, setError, setFields);
        return;
      }
    }

    await logPermissionEvent("become_medic", logView, oldSnapshot);

    if (isDoctorOrDentistType(values.type)) {
      setAccess((draft) => {
        draft.name = "review_status";
      });
    } else {
      window.location.reload();
    }
  }

  return (
    <Form {...form}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Dialog open={isModalOpen} onOpenChange={setModalOpen}>
          <NilDetails closeModal={() => setModalOpen(false)} />
        </Dialog>
        <h2
          className={`text-card-foreground mb-6 text-center text-2xl font-semibold`}
        >
          Gratulujemy ukończenia studiów!
        </h2>
        <p className={`text-foreground mb-6 text-center text-base font-normal`}>
          {trans("permission.actions.student.title")}:
        </p>
        <div style={{ width: "100%" }}>
          <FieldsContainer>
            <SelectField
              field={fields.type}
              onChange={(selected) => {
                setType(selected);
              }}
            />

            {fields.licence && <TextField field={fields.licence} />}

            {fields.birthdayDate && (
              <>
                <DateField field={fields.birthdayDate} />
                <div className="text-muted-foreground mx-auto mb-8 flex flex-col items-center justify-center text-center text-sm font-semibold">
                  <div className="mb-4 flex w-full justify-center">
                    <a
                      href="https://nil.org.pl/"
                      rel="nofollow noreferrer"
                      target="_blank"
                    >
                      <img src={getAssetImage("/profile/nil.png")} alt="nil" />
                    </a>
                  </div>
                  {trans("user.nil.title")}
                  <Button
                    variant={"ghost"}
                    onClick={() => setModalOpen(true)}
                    type={"button"}
                    className={"mt-2 w-full"}
                  >
                    {trans("user.nil.details")}
                  </Button>
                </div>
              </>
            )}

            {fields.firstName && (
              <>
                <TextField field={fields.firstName} />
                <TextField field={fields.secondName} />
              </>
            )}

            {fields.verificationImage && (
              <CroppedImageField
                field={{
                  ...fields.verificationImage,
                  label:
                    isPublicHealthGraduate(type) ||
                    isOccupationalTherapist(type)
                      ? trans("permission.actions.add_proof")
                      : trans("permission.actions.add_diploma"),
                }}
                verificationText={
                  <p
                    className={`text-muted-foreground my-8 text-xs font-normal`}
                  >
                    {trans("registration.diploma.disclaimer")}
                  </p>
                }
                onChange={(data) => {
                  setFields((draft) => {
                    draft.verificationImage.value = data;
                  });
                }}
              />
            )}
          </FieldsContainer>
        </div>

        {!user.defaultNewsletter.isSignedUp && fields.newsletter && (
          <FieldsContainer>
            <CheckboxField field={fields.newsletter} />
          </FieldsContainer>
        )}

        <div className="flex max-w-[400px] flex-col gap-4">
          <Button type="submit">
            {loading && <LoaderIcon className={"animate-spin"} size={16} />}{" "}
            {trans("next")}
          </Button>
          <Button variant={"ghost"} onClick={closeForm}>
            {trans("cancel")}
          </Button>
        </div>

        <FormError error={error} />
      </form>
    </Form>
  );
};
